import React, { useMemo, useState } from 'react'
import classnames from 'classnames'
import KpiStrip from '../components/KpiStrip'
import SaberCard from '../components/SaberCard'
import SaberTable from '../components/SaberTable'
import useEscClose from '../components/useEscClose'
import { SelectControl } from '../components/Controls'
import type { SaberTableColumn } from '../components/SaberTable'
import { useGetRunControlOverviewQuery, useGetRunRequestsQuery, useGetRunSchedulesQuery } from '../saberApi'
import type { RunRequestStatus, RunRequestVO, RunScheduleVO, RunType } from '../saber.types'
import { dateTime, duration, integer } from '../format'
import { CELL_COLOR } from '../cellColor'
import NullDash from '../components/NullDash'
import type { PanelStickyPropsFn } from '../stickyPanel'

// Phase 1 read-only SABER run-control dashboard (saber-run-control-ui-brief.md):
// gauges, Up Next (queued), Run History (filterable, slide-over detail), Schedules.

const POLL_MS = 30000

// ---- helpers (exported for tests) ----

/** Relative time vs `now`: "2 min ago" / "in 12 min" / "5s ago" / "in 1h 4m". */
export const relativeTime = (iso: string | null | undefined, now: Date = new Date()): string => {
  if (iso == null || iso === '') return '—'
  const t = new Date(iso).getTime()
  if (Number.isNaN(t)) return iso
  const diff = t - now.getTime()
  const future = diff > 0
  const sec = Math.round(Math.abs(diff) / 1000)
  let text: string
  if (sec < 60) {
    text = `${sec}s`
  } else if (sec < 3600) {
    text = `${Math.round(sec / 60)} min`
  } else if (sec < 86400) {
    const h = Math.floor(sec / 3600)
    const m = Math.round((sec % 3600) / 60)
    text = m > 0 ? `${h}h ${m}m` : `${h}h`
  } else {
    text = `${Math.round(sec / 86400)}d`
  }
  return future ? `in ${text}` : `${text} ago`
}

/** Render request/schedule params as chip strings; arrays show their count (brief §2.5). */
export const paramChips = (params: Record<string, unknown> | null | undefined): string[] => {
  if (params == null) return []
  return Object.entries(params).map(([key, value]) => {
    if (Array.isArray(value)) return `${key}: ${value.length}`
    return `${key}: ${String(value)}`
  })
}

/** One-line "work done" summary per result_summary shape (brief §2.5). */
export const summarizeResult = (rs: Record<string, unknown> | null | undefined): string => {
  if (rs == null) return '—'
  if (rs.reaped === true) return 'reaped'
  if (rs.lockSkipped === true) return 'lock-skip'
  if (typeof rs.error === 'string') return `error: ${rs.error}`
  if (rs.programMonthsProcessed != null) {
    const processed = Number(rs.programMonthsProcessed)
    const succeeded = Number(rs.programMonthsSucceeded ?? 0)
    const failed = Number(rs.programMonthsFailed ?? 0)
    return `${succeeded}/${processed} program-months${failed > 0 ? ` · ${failed} failed` : ''}`
  }
  if (rs.dayGrainedProcessed != null || rs.sourcesScanned != null) {
    const days = Number(rs.dayGrainedProcessed ?? 0)
    const invoices = Number(rs.invoiceGrainedProcessed ?? 0)
    const failed = Number(rs.dayGrainedFailed ?? 0) + Number(rs.invoiceGrainedFailed ?? 0)
    if (days === 0 && invoices === 0 && failed === 0) return 'no stale work'
    return `${days} day-ranges · ${invoices} invoices${failed > 0 ? ` · ${failed} failed` : ''}`
  }
  return '—'
}

// ---- small presentational pieces ----

const TYPE_CLASSES: Record<RunType, string> = {
  INCREMENTAL: 'bg-indigo-100 text-indigo-800 ring-indigo-600/20 dark:bg-indigo-400/10 dark:text-indigo-300 dark:ring-indigo-400/30',
  LOOKBACK: 'bg-sky-100 text-sky-800 ring-sky-600/20 dark:bg-sky-400/10 dark:text-sky-300 dark:ring-sky-400/30',
  TARGETED: 'bg-purple-100 text-purple-800 ring-purple-600/20 dark:bg-purple-400/10 dark:text-purple-300 dark:ring-purple-400/30'
}

const TypeBadge: React.FC<{ type: RunType }> = ({ type }) => (
  <span className={classnames('inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset', TYPE_CLASSES[type])}>
    {type}
  </span>
)

// Lifecycle colors per brief §4.1: QUEUED grey hollow, CLAIMED blue, SUCCEEDED
// green, FAILED red, CANCELLED muted strikethrough; lock-skip requeue amber.
const STATUS_CLASSES: Record<RunRequestStatus, string> = {
  QUEUED: 'bg-transparent text-slate-600 ring-slate-400/60 dark:text-slate-300 dark:ring-slate-500/60',
  CLAIMED: 'bg-blue-100 text-blue-800 ring-blue-600/30 dark:bg-blue-400/10 dark:text-blue-300 dark:ring-blue-400/30',
  SUCCEEDED: 'bg-green-100 text-green-800 ring-green-600/40 dark:bg-green-400/10 dark:text-green-400 dark:ring-green-400/20',
  FAILED: 'bg-red-100 text-red-800 ring-red-600/20 dark:bg-red-400/10 dark:text-red-400 dark:ring-red-400/50',
  CANCELLED: 'bg-slate-100 text-slate-500 line-through ring-slate-400/30 dark:bg-slate-400/10 dark:text-slate-400 dark:ring-slate-400/20'
}

const REQUEUED_CLASSES =
  'bg-amber-100 text-amber-800 ring-amber-600/30 dark:bg-amber-400/10 dark:text-amber-400 dark:ring-amber-400/30'

const RequestStatusBadge: React.FC<{ request: RunRequestVO }> = ({ request }) => {
  // A QUEUED row whose result carries lockSkipped is a requeued lock-skip — amber, informational.
  const requeued = request.status === 'QUEUED' && request.resultSummary?.lockSkipped === true
  return (
    <span
      className={classnames(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset',
        requeued ? REQUEUED_CLASSES : STATUS_CLASSES[request.status]
      )}
    >
      {requeued ? 'REQUEUED' : request.status}
    </span>
  )
}

const ParamChips: React.FC<{ params: Record<string, unknown> | null }> = ({ params }) => {
  const chips = paramChips(params)
  if (chips.length === 0) return <NullDash />
  return (
    <span className="flex flex-wrap gap-1">
      {chips.map((chip) => (
        <span
          key={chip}
          className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 font-mono text-[0.7rem] text-slate-600 dark:bg-slate-700 dark:text-slate-300"
        >
          {chip}
        </span>
      ))}
    </span>
  )
}

const DetailRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <dt className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}</dt>
    <dd className="text-slate-900 dark:text-slate-100">{children}</dd>
  </div>
)

const JsonBlock: React.FC<{ value: unknown }> = ({ value }) => (
  <pre className="overflow-x-auto rounded-md bg-slate-50 p-3 font-mono text-xs text-slate-700 dark:bg-slate-800 dark:text-slate-200">
    {JSON.stringify(value, null, 2)}
  </pre>
)

// ---- slide-over: request detail (pattern copied from VarianceExplorer) ----

const RequestDetailDrawer: React.FC<{ request: RunRequestVO; onClose: () => void }> = ({ request, onClose }) => {
  useEscClose(true, onClose)
  const reaped = request.resultSummary?.reaped === true
  const lockSkipped = request.resultSummary?.lockSkipped === true

  return (
    <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true" aria-label={`Request #${request.id} detail`}>
      <button type="button" aria-label="Close" className="flex-1 bg-black/40" onClick={onClose} />
      <div className="h-full w-full max-w-xl overflow-y-auto bg-white shadow-xl dark:bg-slate-900">
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3 dark:border-slate-700">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Request #{request.id}</h3>
            <TypeBadge type={request.runType} />
            <RequestStatusBadge request={request} />
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-700"
          >
            Close
          </button>
        </div>

        <div className="space-y-4 p-4">
          {reaped && (
            <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-400/30 dark:bg-red-400/10 dark:text-red-400">
              Container died mid-run; reaped by the 90-min watchdog.
            </p>
          )}
          {lockSkipped && (
            <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700 dark:border-amber-400/30 dark:bg-amber-400/10 dark:text-amber-400">
              Lock held at dispatch — automatically deferred to {dateTime(request.notDueBefore)}.
            </p>
          )}

          <SaberCard title="Lifecycle">
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              <DetailRow label="Created">{dateTime(request.createdAt)}</DetailRow>
              <DetailRow label="Requested by">{request.requestedBy}</DetailRow>
              <DetailRow label="Eligible at">{dateTime(request.notDueBefore)}</DetailRow>
              <DetailRow label="Priority">{integer(request.priority)}</DetailRow>
              <DetailRow label="Claimed">{dateTime(request.claimedAt)}</DetailRow>
              <DetailRow label="Finished">{dateTime(request.finishedAt)}</DetailRow>
              <DetailRow label="Duration">{duration(request.durationMs == null ? null : request.durationMs / 1000)}</DetailRow>
              <DetailRow label="Schedule">
                {request.scheduleName != null ? request.scheduleName : <span className="text-slate-400 dark:text-slate-500">manual one-off</span>}
              </DetailRow>
              <DetailRow label="Pipeline run">
                {request.pipelineRunId != null ? `#${request.pipelineRunId}` : '—'}
              </DetailRow>
            </dl>
          </SaberCard>

          <SaberCard title="Params">
            <div className="space-y-3">
              <ParamChips params={request.params} />
              {Object.keys(request.params ?? {}).length > 0 && <JsonBlock value={request.params} />}
            </div>
          </SaberCard>

          <SaberCard title="Result">
            {request.resultSummary == null ? (
              <p className="text-sm text-slate-400 dark:text-slate-500">No result yet.</p>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-slate-700 dark:text-slate-200">{summarizeResult(request.resultSummary)}</p>
                <JsonBlock value={request.resultSummary} />
              </div>
            )}
          </SaberCard>
        </div>
      </div>
    </div>
  )
}

// ---- main screen ----

type TypeFilter = RunType | 'ALL'
type StatusFilter = RunRequestStatus | 'ALL'

const RUN_TYPES: RunType[] = ['INCREMENTAL', 'LOOKBACK', 'TARGETED']
const RUN_STATUSES: RunRequestStatus[] = ['QUEUED', 'CLAIMED', 'SUCCEEDED', 'FAILED', 'CANCELLED']

export interface EngineStatusProps {
  panelStickyProps?: PanelStickyPropsFn
}

export const EngineStatus: React.FC<EngineStatusProps> = ({ panelStickyProps = () => ({}) }) => {
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('ALL')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL')
  const [selected, setSelected] = useState<RunRequestVO | null>(null)

  // Dashboard liveness: poll the overview + both request lists (brief §4.1).
  const { data: overview, isError: overviewError } = useGetRunControlOverviewQuery(undefined, {
    pollingInterval: POLL_MS
  })
  const {
    data: queuedPage,
    isLoading: queuedLoading,
    isError: queuedError
  } = useGetRunRequestsQuery({ status: 'QUEUED', limit: 10 }, { pollingInterval: POLL_MS })
  const {
    data: historyPage,
    isLoading: historyLoading,
    isError: historyError
  } = useGetRunRequestsQuery(
    {
      limit: 25,
      runType: typeFilter === 'ALL' ? undefined : typeFilter,
      status: statusFilter === 'ALL' ? undefined : statusFilter
    },
    { pollingInterval: POLL_MS }
  )
  const { data: schedules, isLoading: schedulesLoading, isError: schedulesError } = useGetRunSchedulesQuery()

  const now = new Date()
  const running = overview?.running ?? null
  const elapsed =
    running?.claimedAt != null ? duration((now.getTime() - new Date(running.claimedAt).getTime()) / 1000) : '—'

  // Up Next: QUEUED ordered by priority asc, then createdAt asc (brief §6.1).
  const queued = useMemo(() => {
    const rows = queuedPage?.requests ?? []
    return [...rows].sort((a, b) => a.priority - b.priority || a.createdAt.localeCompare(b.createdAt))
  }, [queuedPage])

  // Earliest enabled schedule fire — used for the "queue empty" hint.
  const nextFireAt = useMemo(() => {
    const enabled = (schedules ?? []).filter((s) => s.enabled)
    if (enabled.length === 0) return null
    return enabled.reduce((min, s) => (s.nextFireAt < min ? s.nextFireAt : min), enabled[0].nextFireAt)
  }, [schedules])

  const upNextColumns: Array<SaberTableColumn<RunRequestVO>> = [
    { key: 'id', header: '#', render: (r) => <span className="font-mono text-xs">#{r.id}</span> },
    { key: 'type', header: 'Type', render: (r) => <TypeBadge type={r.runType} /> },
    { key: 'params', header: 'Params', render: (r) => <ParamChips params={r.params} /> },
    {
      key: 'eligible',
      header: 'Eligible at',
      render: (r) => {
        const future = new Date(r.notDueBefore).getTime() > now.getTime()
        return (
          <span title={dateTime(r.notDueBefore)}>
            {future ? (
              <span className={CELL_COLOR.warning}>deferred · {relativeTime(r.notDueBefore, now)}</span>
            ) : (
              'now'
            )}
          </span>
        )
      }
    },
    { key: 'requestedBy', header: 'Requested by', render: (r) => r.requestedBy }
  ]

  const historyColumns: Array<SaberTableColumn<RunRequestVO>> = [
    { key: 'id', header: '#', sortable: true, sortValue: (r) => r.id, render: (r) => <span className="font-mono text-xs">#{r.id}</span> },
    { key: 'type', header: 'Type', sortable: true, sortValue: (r) => r.runType, render: (r) => <TypeBadge type={r.runType} /> },
    { key: 'status', header: 'Status', align: 'center', sortable: true, sortValue: (r) => r.status, render: (r) => <RequestStatusBadge request={r} /> },
    {
      key: 'work',
      header: 'Work done',
      render: (r) => {
        const text = summarizeResult(r.resultSummary)
        const isError = typeof r.resultSummary?.error === 'string'
        return <span className={isError ? CELL_COLOR.negative : CELL_COLOR.muted}>{text}</span>
      }
    },
    {
      key: 'duration',
      header: 'Duration',
      align: 'right',
      sortable: true,
      sortValue: (r) => r.durationMs,
      render: (r) => duration(r.durationMs == null ? null : r.durationMs / 1000)
    },
    { key: 'requestedBy', header: 'Requested by', sortable: true, sortValue: (r) => r.requestedBy, render: (r) => r.requestedBy },
    {
      key: 'finished',
      header: 'Finished at',
      sortable: true,
      sortValue: (r) => r.finishedAt,
      render: (r) => <span title={r.finishedAt ?? undefined}>{dateTime(r.finishedAt)}</span>
    }
  ]

  const scheduleColumns: Array<SaberTableColumn<RunScheduleVO>> = [
    { key: 'name', header: 'Name', sortable: true, sortValue: (s) => s.name, render: (s) => <span className={classnames('font-mono text-xs', CELL_COLOR.secondary)}>{s.name}</span> },
    { key: 'type', header: 'Type', sortable: true, sortValue: (s) => s.runType, render: (s) => <TypeBadge type={s.runType} /> },
    { key: 'cadence', header: 'Cadence', sortable: true, sortValue: (s) => s.cadence, render: (s) => <span className={classnames('font-mono text-xs', CELL_COLOR.secondary)}>{s.cadence}</span> },
    {
      key: 'nextFire',
      header: 'Next fire',
      sortable: true,
      sortValue: (s) => s.nextFireAt,
      render: (s) => <span title={dateTime(s.nextFireAt)}>{relativeTime(s.nextFireAt, now)}</span>
    },
    {
      key: 'enabled',
      header: 'Enabled',
      align: 'center',
      sortable: true,
      sortValue: (s) => (s.enabled ? 1 : 0),
      render: (s) =>
        s.enabled ? (
          <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800 ring-1 ring-inset ring-green-600/40 dark:bg-green-400/10 dark:text-green-400 dark:ring-green-400/20">
            enabled
          </span>
        ) : (
          <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500 ring-1 ring-inset ring-slate-400/30 dark:bg-slate-400/10 dark:text-slate-400 dark:ring-slate-400/20">
            paused
          </span>
        )
    },
    { key: 'priority', header: 'Priority', align: 'right', sortable: true, sortValue: (s) => s.priority, render: (s) => integer(s.priority) }
  ]

  return (
    <div className="space-y-4">
      {overviewError && (
        <p className="text-sm text-red-600 dark:text-red-400">Failed to load run-control overview.</p>
      )}

      <KpiStrip
        items={[
          {
            label: 'Running',
            value:
              running != null ? (
                <span className="flex items-center gap-2">
                  <TypeBadge type={running.runType} />
                  <span>{elapsed}</span>
                </span>
              ) : (
                <span className="text-slate-400 dark:text-slate-500">Idle</span>
              ),
            sublabel: running != null ? `#${running.id} · claimed ${relativeTime(running.claimedAt, now)}` : 'no claimed request'
          },
          {
            label: 'Queue depth',
            value: integer(overview?.queueDepth),
            sublabel: 'queued requests'
          },
          {
            label: 'Backlog',
            value: `${integer(overview?.pendingInvoices)} invoices · ${integer(overview?.pendingProgramDays)} program-days`,
            sublabel: 'pending rebuild flags'
          },
          {
            label: 'Last tick',
            value: <span title={dateTime(overview?.lastTickAt)}>{relativeTime(overview?.lastTickAt, now)}</span>,
            sublabel: 'cron drains the queue every 10 min'
          }
        ]}
      />

      <SaberCard title="Up Next" {...panelStickyProps(1)}>
        {queuedError ? (
          <p className="py-6 text-center text-sm text-red-600 dark:text-red-400">Failed to load queued requests.</p>
        ) : queuedLoading ? (
          <div className="py-6 text-center text-sm text-slate-400 dark:text-slate-500">Loading…</div>
        ) : (
          <SaberTable
            columns={upNextColumns}
            rows={queued}
            rowKey={(r) => r.cuid}
            onRowClick={(r) => setSelected(r)}
            emptyMessage={
              nextFireAt != null
                ? `Queue empty — next schedule fires ${relativeTime(nextFireAt, now)}.`
                : 'Queue empty.'
            }
            helpKey="upNext"
          />
        )}
      </SaberCard>

      <SaberCard
        title="Run History"
        {...panelStickyProps(2)}
        actions={
          <>
            <SelectControl
              aria-label="Filter by type"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as TypeFilter)}
              className="text-xs"
            >
              <option value="ALL">All types</option>
              {RUN_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </SelectControl>
            <SelectControl
              aria-label="Filter by status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
              className="text-xs"
            >
              <option value="ALL">All statuses</option>
              {RUN_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </SelectControl>
          </>
        }
      >
        {historyError ? (
          <p className="py-6 text-center text-sm text-red-600 dark:text-red-400">Failed to load run history.</p>
        ) : historyLoading ? (
          <div className="py-6 text-center text-sm text-slate-400 dark:text-slate-500">Loading…</div>
        ) : (
          <SaberTable
            columns={historyColumns}
            rows={historyPage?.requests ?? []}
            rowKey={(r) => r.cuid}
            onRowClick={(r) => setSelected(r)}
            emptyMessage="No run requests match the filters."
            helpKey="runHistory"
          />
        )}
      </SaberCard>

      <SaberCard title="Schedules" {...panelStickyProps(3)}>
        {schedulesError ? (
          <p className="py-6 text-center text-sm text-red-600 dark:text-red-400">Failed to load schedules.</p>
        ) : schedulesLoading ? (
          <div className="py-6 text-center text-sm text-slate-400 dark:text-slate-500">Loading…</div>
        ) : (
          <SaberTable columns={scheduleColumns} rows={schedules ?? []} rowKey={(s) => s.cuid} emptyMessage="No schedules." helpKey="schedules" />
        )}
      </SaberCard>

      {selected != null && <RequestDetailDrawer request={selected} onClose={() => setSelected(null)} />}
    </div>
  )
}

export default EngineStatus
